// frontend/src/pages/PaymentSuccess.tsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { A } from "../styles/appleClasses";

type ResolveOut = {
  invoice_id: number;
  sale_id: number | null;
  company_id: number;
  status?: string | null;
};

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");

  const [msg, setMsg] = useState("Finalizing payment…");
  const [err, setErr] = useState<string | null>(null);

  // prevent infinite retries / timers on unmount
  const attemptsRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  const schedule = (fn: () => void, ms: number) => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(fn, ms);
  };

  const finalizeOnce = useCallback(async () => {
    if (!sessionId) {
      setErr("Missing session id.");
      setMsg("Unable to finalize payment.");
      return;
    }

    // cap retries to avoid endless loops
    attemptsRef.current += 1;
    const attempt = attemptsRef.current;

    // after many attempts, stop trying quietly and show a helpful message
    if (attempt > 12) {
      setMsg("Payment is still processing.");
      setErr("If this doesn’t resolve soon, go back to Sales and refresh the invoice status.");
      return;
    }

    setErr(null);

    try {
      // 1) Resolve the session → invoice & sale
      const res = await apiGet<ResolveOut>(
        `/payments/session/${encodeURIComponent(sessionId)}/resolve`
      );
      const invoiceId = res.invoice_id;
      const saleId = res.sale_id;

      // 2) Finalize (idempotent). Persists fees, posts CP+JE, recomputes status.
      const fin = await apiPost<{
        ok: boolean;
        ready: boolean;
        invoice_status?: string;
      }>(`/payments/invoices/${invoiceId}/finalize`, {});

      if (fin.ok && fin.ready) {
        setMsg("Payment confirmed. Redirecting…");
        schedule(() => {
          if (saleId) {
            navigate(companyRoute(company_id!, `/sales/${saleId}`), { replace: true });
          } else {
            navigate(companyRoute(company_id!, "/sales"), { replace: true });
          }
        }, 800);
        return;
      }

      // PI may still be 'processing'. Poll a few times.
      setMsg("Waiting for Stripe to confirm…");
      schedule(finalizeOnce, 1500);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to finalize. Retrying…");
      schedule(finalizeOnce, 2000);
    }
  }, [company_id, navigate, sessionId]);

  useEffect(() => {
    finalizeOnce();
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [finalizeOnce]);

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body} flex items-center justify-center px-4 py-10`}>
      <div className={`${A.card} w-full max-w-md px-6 py-5 text-center`}>
        <div className={`${A.title} mb-2`}>Thanks!</div>

        <div className={A.label}>{msg}</div>

        {err && (
          <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            {err}
          </div>
        )}

        <div className="text-[11px] text-gray-400 mt-4">
          You can close this tab after it finishes.
        </div>
      </div>
    </div>
  );
}
